import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Relative, ShadowOverlay } from 'pcln-design-system'
import InputModal from '@pcln/input-modal'
import Calendar from '../Calendar'
import DateField from '../DateField'
import formatDates from '../utils/formatDates'
import { IDatePickerProps } from '../types'

const StyledCalendar = styled(Calendar)`
  position: ${(props) => props.position || 'absolute'};
  ${({ rightAlignCalendar }: { rightAlignCalendar: boolean }) => rightAlignCalendar && 'right: 0;'}
  z-index: ${(props) => props.zIndex};
`

const StyledShadowOverlay = styled(ShadowOverlay)`
  ${(props) =>
    props.hide &&
    `
    background-color: transparent;
  `}
`

const defaultInitialDates = { startDate: null, endDate: null }

function DatePicker({
  className,
  errorMessage,
  hasShadowEffect = true,
  height,
  initialDates = defaultInitialDates,
  isInitiallyOpen,
  isMobile,
  label = 'Check-in - Check-out',
  name,
  rightAlignCalendar,
  selectTwoDates = true,
  width,
  zIndex = 2,
  onBlur,
  onChange,
  onFocus,
  ...calendarProps
}: IDatePickerProps) {
  const [isOpen, setOpen] = useState(isInitiallyOpen)
  const [dates, setDates] = useState(initialDates)

  const hideTooltip = useMemo(() => calendarProps?.hideTooltip || isOpen, [calendarProps.hideTooltip, isOpen])
  const dateString = useMemo(() => formatDates(selectTwoDates, dates), [selectTwoDates, dates])

  useEffect(() => {
    setDates(initialDates)
  }, [initialDates])

  const handleOpen = useCallback(() => {
    setOpen(true)
    onFocus?.()
  }, [isMobile, onFocus])

  const handleFocus = useCallback(() => {
    if (isMobile) {
      return
    }

    setOpen(true)
    onFocus?.()
  }, [isMobile, onFocus])

  const handleClose = useCallback(
    (evt) => {
      setOpen(false)
      onBlur?.()
      onChange?.(dates)
    },
    [dates, onBlur, onChange]
  )

  const handleChange = useCallback((evt, dates) => {
    setDates(dates)
    if (dates.endDate !== null) {
      setOpen(false)
      onBlur?.()
      onChange?.(dates)
    }
  }, [])

  return (
    <Relative>
      {isOpen && !isMobile && (
        <StyledShadowOverlay
          hide={!hasShadowEffect}
          zIndex={zIndex}
          onClick={handleClose}
          data-testid='date-picker-overlay'
        />
      )}
      <DateField
        className={className}
        error={errorMessage}
        height={height}
        hideTooltip={hideTooltip}
        isMobile={isMobile}
        label={label}
        name={name}
        value={dateString}
        width={width}
        zIndex={isOpen ? zIndex : 0}
        onClick={handleOpen}
        onFocus={handleFocus}
        mb={2}
      />
      {isOpen && !isMobile && (
        <StyledCalendar
          zIndex={zIndex + 1}
          rightAlignCalendar={rightAlignCalendar}
          selectTwoDates={selectTwoDates}
          startDate={dates.startDate}
          endDate={dates.endDate}
          onChange={handleChange}
          onDismiss={handleClose}
          {...calendarProps}
        />
      )}
      {isMobile && (
        <InputModal
          id='date-picker-modal'
          isOpen={isOpen}
          input={
            <DateField
              error={errorMessage}
              hideTooltip={hideTooltip}
              isMobile
              label={label}
              value={dateString}
              width={1}
              name={name}
            />
          }
          inputProps={{ alignItems: 'center', px: '12px', py: '12px' }}
          zIndex={zIndex}
          onClose={handleClose}
        >
          <Calendar
            startDate={dates.startDate}
            endDate={dates.endDate}
            isMobile
            selectTwoDates={selectTwoDates}
            onChange={handleChange}
            onDismiss={handleClose}
            {...calendarProps}
          />
        </InputModal>
      )}
    </Relative>
  )
}

export default memo(DatePicker)
